import { useState, useEffect, useRef } from 'react';

interface RefreshState {
  isRefreshing: boolean;
  shouldShow: boolean;
}

/**
 * Hook to detect manual page refresh/swipe down action
 * Returns refresh state for animation: isRefreshing (during gesture) and shouldShow (for display)
 * Resets when component unmounts (e.g., when switching tabs)
 *
 * @param tabId Optional identifier for the tab (e.g., 'Following', 'Featured')
 *              If provided, refresh state is tracked per tab
 * @param loading Optional loading state to detect when refresh completes
 */
export const useRefreshDetection = (tabId?: string, loading?: boolean): RefreshState => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const touchStartYRef = useRef<number | null>(null);
  const isAtTopRef = useRef<boolean>(true);
  const componentMountedRef = useRef<boolean>(false);
  const refreshTimeoutRef = useRef<number | null>(null);
  const wasLoadingRef = useRef<boolean>(false);
  const isRefreshingRef = useRef<boolean>(false);

  useEffect(() => {
    // Reset state when component mounts (e.g., when switching tabs)
    isRefreshingRef.current = false;
    setIsRefreshing(false);
    setShouldShow(false);
    componentMountedRef.current = true;
    wasLoadingRef.current = loading ?? false;

    // Don't show title on initial page load - only show during active refresh gestures

    // Detect pull-to-refresh gesture: user scrolls to top then pulls down
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      isAtTopRef.current = scrollTop === 0;
    };

    // Detect touch events for pull-to-refresh on mobile
    const handleTouchStart = (e: TouchEvent) => {
      if (isAtTopRef.current && componentMountedRef.current) {
        touchStartYRef.current = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isAtTopRef.current || touchStartYRef.current === null || !componentMountedRef.current) return;

      const touchY = e.touches[0].clientY;
      const pullDistance = touchY - touchStartYRef.current;

      // If user pulls down more than 60px while at top, start refresh
      if (pullDistance > 60 && !isRefreshingRef.current) {
        isRefreshingRef.current = true;
        setIsRefreshing(true);
        setShouldShow(true);
      }
    };

    const handleTouchEnd = () => {
      // If refresh was triggered but no loading state detected, hide after timeout
      if (isRefreshingRef.current && !(loading ?? false)) {
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
        }
        refreshTimeoutRef.current = window.setTimeout(() => {
          isRefreshingRef.current = false;
          setIsRefreshing(false);
          setShouldShow(false);
        }, 2000);
      }
      touchStartYRef.current = null;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      componentMountedRef.current = false;
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      // Reset state when component unmounts
      isRefreshingRef.current = false;
      setIsRefreshing(false);
      setShouldShow(false);
    };
  }, [tabId, loading]);

  // Detect when refresh completes (loading changes from true to false)
  useEffect(() => {
    if (isRefreshingRef.current && loading) {
      // Refresh is in progress
      wasLoadingRef.current = true;
      // Clear any existing timeout since we're actually refreshing
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    } else if (isRefreshingRef.current && wasLoadingRef.current && !loading) {
      // Refresh completed, slide out immediately
      isRefreshingRef.current = false;
      setIsRefreshing(false);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      // Hide immediately when refresh completes
      setShouldShow(false);
    } else if (!isRefreshingRef.current) {
      wasLoadingRef.current = loading ?? false;
    }
  }, [loading]);

  return { isRefreshing, shouldShow };
};
